"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Lenis from "lenis";
import { useReducedMotion } from "@/hooks/useReducedMotion";

/**
 * App-wide smooth scrolling via Lenis. Mounted once in the root layout so every
 * page shares one scroll instance. Disabled entirely under prefers-reduced-motion
 * (native scrolling is used instead). Exposes the instance on `window.__lenis`
 * so anchor links and scroll-driven components can drive it without prop-drilling.
 */
declare global {
  interface Window {
    __lenis?: Lenis;
  }
}

export function SmoothScroll({ children }: { children: React.ReactNode }) {
  const reducedMotion = useReducedMotion();
  const pathname = usePathname();

  useEffect(() => {
    if (reducedMotion) return;

    // На сенсорных устройствах плавную прокрутку не включаем. Палец и так
    // прокручивает плавно, разницы не видно, а Lenis пересчитывает положение
    // страницы на каждом кадре и держит основной поток занятым: на замере
    // Lighthouse это давало 28 секунд блокировки и 40 секунд работы в потоке
    // на эмуляции обычного телефона. Проверяем именно тип указателя, а не
    // ширину экрана: узкое окно на компьютере - всё ещё мышь.
    const coarsePointer =
      typeof window !== "undefined" &&
      window.matchMedia?.("(pointer: coarse)").matches === true;
    if (coarsePointer) return;

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    window.__lenis = lenis;

    let frame = 0;
    const raf = (time: number) => {
      lenis.raf(time);
      frame = requestAnimationFrame(raf);
    };
    frame = requestAnimationFrame(raf);

    // Intercept in-page anchor clicks so they scroll smoothly through Lenis.
    const onClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest?.("a[href^='#']");
      if (!target) return;
      const id = target.getAttribute("href");
      if (!id || id === "#") return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el as HTMLElement, { offset: -72 });
    };
    document.addEventListener("click", onClick);

    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("click", onClick);
      lenis.destroy();
      window.__lenis = undefined;
    };
  }, [reducedMotion]);

  // On client navigation (e.g. gallery card -> /work/[slug]), the persistent
  // Lenis instance keeps the previous scroll offset, landing the new page
  // mid-way. On every route change, jump to the hash target if the URL has one
  // (e.g. /#work when going back), otherwise to the top. rAF lets the new page
  // mount before we measure the target.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const hash = window.location.hash;
      const el = hash && hash.length > 1 ? document.querySelector(hash) : null;
      if (window.__lenis) {
        if (el) window.__lenis.scrollTo(el as HTMLElement, { immediate: true, offset: -72 });
        else window.__lenis.scrollTo(0, { immediate: true });
      } else if (el) {
        (el as HTMLElement).scrollIntoView();
      } else {
        window.scrollTo(0, 0);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return <>{children}</>;
}
